import { createRootRoute, createRoute, createRouter, redirect, Outlet } from '@tanstack/react-router';
import { NAV_ENTRIES, DEFAULT_PATH } from '@procuai/ui';
import { AppShellLayout } from './routes/AppShell.js';
import { ModulePage } from './routes/ModulePage.js';
import { Login } from './routes/Login.js';
import { Verify } from './routes/Verify.js';
import { Portal } from './routes/Portal.js';
import { BankValidatePage } from './routes/BankValidatePage.js';
import { PortalLoginPage } from './routes/PortalLoginPage.js';
import { LegalPage } from './routes/LegalPage.js';

const rootRoute = createRootRoute({ component: () => <Outlet /> });

const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/login', component: Login });
const verifyRoute = createRoute({ getParentRoute: () => rootRoute, path: '/auth/verify', component: Verify });
// Public supplier portal (BP-02 §2.4): a no-login, no-shell page opened from an emailed token link.
const portalRoute = createRoute({ getParentRoute: () => rootRoute, path: '/portal/$token', component: Portal });
// Public bank-info validation (D-141): a no-login, no-shell page the supplier's finance colleague opens to validate.
const portalBankValidateRoute = createRoute({ getParentRoute: () => rootRoute, path: '/portal/bank-validate/$token', component: BankValidatePage });
// Public supplier sign-in (D-102): a no-login, no-shell page where a supplier requests a return link by email.
const portalLoginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/portal-login', component: PortalLoginPage });
// Public legal pages (BP-22): the two URLs the Customer Terms cite. No login, no shell.
const legalPrivacyRoute = createRoute({ getParentRoute: () => rootRoute, path: '/legal/privacy', component: () => <LegalPage docType="privacy_policy" /> });
const legalSubprocessorsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/legal/subprocessors', component: () => <LegalPage docType="subprocessor_list" /> });

// Authenticated layout: the shell wraps every module route (its content lands in the Outlet).
const shellLayout = createRoute({ getParentRoute: () => rootRoute, id: 'shell', component: AppShellLayout });

// '/' redirects to the default module (the approved Savings Pipeline view).
const indexRoute = createRoute({
  getParentRoute: () => shellLayout,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: DEFAULT_PATH as never });
  },
});

// One route per navigable module; content is resolved by ModulePage.
const moduleRoutes = NAV_ENTRIES.map((entry) =>
  createRoute({
    getParentRoute: () => shellLayout,
    path: entry.path,
    component: () => <ModulePage path={entry.path} />,
  }),
);

const routeTree = rootRoute.addChildren([
  loginRoute,
  verifyRoute,
  portalRoute,
  portalBankValidateRoute,
  portalLoginRoute,
  legalPrivacyRoute,
  legalSubprocessorsRoute,
  shellLayout.addChildren([indexRoute, ...moduleRoutes]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
